<template>
  <modal v-if="active" width="560px" :title="t('Temporary password')" @close="close">
    <div class="temporary-password-dialog">
      <div class="temporary-password-dialog__content">
        <ui-info color="red">
          {{
            t(
              'Important notice! Don’t share your temporary password with anyone except BeMob support team managers.',
            )
          }}
        </ui-info>

        <div class="temporary-password-dialog__row">
          <ui-copy-input :label="t('Temporary password')" :value="form.password" />
        </div>
        <div class="temporary-password-dialog__row">
          <div class="temporary-password-dialog__notice">
            {{ t('This password can be used only once till') }} <b>{{ form.expireAt }}</b>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12" style="display: flex">
        <ui-button
          :text="t('Close')"
          icon="fas fa-times"
          :disabled="loading"
          @click="close"
        />
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import moment from 'moment';
import { onMounted, ref } from 'vue';
import { useI18n } from '@/composables';
import { useStore } from '@/store';
import { getUserTemporaryPasswordApiRequest } from '@/services/api/user-api';

type TemporaryPasswordForm = {
  password?: string;
  expireAt?: string;
};

const props = withDefaults(
  defineProps<{
    format?: string;
  }>(),
  {
    format: 'DD MMMM YYYY HH:mm',
  },
);

const emit = defineEmits<{
  close: [];
}>();

const store = useStore();
const { t } = useI18n();

const active = ref(false);
const loading = ref(false);
const form = ref<TemporaryPasswordForm>({});

const loadTemporaryPassword = async () => {
  store.setLoading(true);
  loading.value = true;
  try {
    const payload = await getUserTemporaryPasswordApiRequest();
    form.value = {
      ...payload,
      expireAt: payload.expireAt
        ? moment(moment.utc(payload.expireAt).toDate()).local().format(props.format)
        : '',
    };
    active.value = true;
  } finally {
    loading.value = false;
    store.setLoading(false);
  }
};

const close = () => {
  active.value = false;
  emit('close');
};

onMounted(() => {
  loadTemporaryPassword();
});
</script>

<style lang="scss" scoped>
.temporary-password-dialog {
  height: 100%;
  max-height: 375px !important;

  > :deep(.v-card) {
    height: 100%;
    max-height: 375px;
  }

  @media (max-width: 600px) {
    .temporary-password-dialog__content {
      padding: 0 12px;
    }
  }
}

.temporary-password-dialog__content {
  margin: -24px;
  padding: 24px;
  overflow-y: auto;
}

.temporary-password-dialog__row {
  margin-top: 16px;
}
</style>
